import { useAtom } from 'jotai';
import { useState } from 'react';
import { Badge, Button, Flex, Link, Spinner, Switch, Text } from '@optiaxiom/react';
import { IconAgent, IconCodeBranch, IconSparkles, IconXmark } from '@optimizely/axiom-icons';

import { opalUserAtom } from '@frontend/atoms';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { useInstanceId } from '@frontend/hooks';

import {
  useAgentDirectoryDetails,
  useInstallAgentFromDirectory,
} from '../../hooks/useAgentDirectory';
import { DirectoryAgent } from '../../services/agent-directory';

type AgentDetailsSidebarProps = {
  agent: DirectoryAgent | undefined;
  open: boolean;
  onClose: () => void;
};

const AgentTypeIcon = {
  specialized: <IconAgent size="16" />,
  workflow: <IconCodeBranch size="16" />,
};

export function AgentDetailsSidebar({ agent, open, onClose }: AgentDetailsSidebarProps) {
  const [opalUser] = useAtom(opalUserAtom);
  const { instanceId } = useInstanceId();
  const [forceUpdate, setForceUpdate] = useState(false);

  const { data: agentDetails, isPending } = useAgentDirectoryDetails(
    agent?.agent_id,
    agent?.directory_version
  );

  const { mutate: installAgent, isPending: isInstalling } = useInstallAgentFromDirectory();

  const agentIcon = agent ? AgentTypeIcon[agent.agent_type] || <IconSparkles size="16" /> : null;

  const handleInstall = () => {
    if (!agent || !instanceId || !opalUser?.id) return;

    installAgent({
      agentId: agent.agent_id,
      request: {
        opal_instance_id: instanceId,
        user_id: opalUser.id,
        directory_version: agent.directory_version,
        force_update: forceUpdate,
      },
    });
  };

  return (
    <Sidebar open={open} dock="right" initialWidth={420} minWidth={300} maxWidth={600}>
      <Flex flexDirection="column" gap="16" p="24">
        {/* Header */}
        <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
          <Flex flexDirection="row" alignItems="center" gap="12">
            <Flex
              p="12"
              rounded="md"
              bg="bg.accent.subtle"
              color="fg.accent"
              alignItems="center"
              justifyContent="center"
            >
              {agentIcon}
            </Flex>
            <Flex gap="0">
              <Text fontSize="lg" fontWeight="600" color="fg.default">
                {agentDetails?.name || 'Agent Details'}
              </Text>
              <Text fontSize="sm" color="fg.secondary">
                @{agent?.agent_id || 'Unknown Agent ID'}
              </Text>
            </Flex>
          </Flex>
          <Button
            icon={<IconXmark size="16" />}
            appearance="subtle"
            onClick={onClose}
            aria-label="Close sidebar"
          />
        </Flex>

        {/* Content */}
        {isPending ? (
          <Flex justifyContent="center" py="24">
            <Spinner>Loading agent details...</Spinner>
          </Flex>
        ) : agent && agentDetails ? (
          <Flex flexDirection="column" gap="16">
            {/* Description */}
            <Flex flexDirection="column" gap="8">
              <Text fontSize="sm" fontWeight="500" color="fg.secondary">
                Description
              </Text>
              <Text fontSize="sm" color="fg.default">
                {agentDetails.description || 'No description available'}
              </Text>
            </Flex>

            {agentDetails?.details_url && (
              <Flex>
                <Link href={agentDetails?.details_url} external>
                  View Documentation
                </Link>
              </Flex>
            )}

            {/* Tags */}
            {agentDetails.tags && agentDetails.tags.length > 0 && (
              <Flex flexDirection="column" gap="8" w="full">
                <Text fontSize="sm" fontWeight="500" color="fg.secondary">
                  Tags
                </Text>
                <Flex flexDirection="row" gap="8" flexWrap="wrap">
                  {agentDetails.tags.map((tag, index) => (
                    <Badge truncate={false} key={tag.name || `tag-${index}`} intent="neutral">
                      {tag.name}
                    </Badge>
                  ))}
                </Flex>
              </Flex>
            )}

            {/* Installation Options */}
            <Flex flexDirection="column" gap="8">
              <Text fontSize="sm" fontWeight="500" color="fg.secondary">
                Installation Options
              </Text>
              <Switch checked={forceUpdate} onCheckedChange={setForceUpdate}>
                Force Update (overwrite existing agent)
              </Switch>
            </Flex>

            {/* Activate Button */}
            <Button
              appearance="primary"
              onClick={handleInstall}
              w="fit"
              disabled={isInstalling || !instanceId || !opalUser?.id}
            >
              {isInstalling ? 'Installing...' : 'Activate'}
            </Button>
          </Flex>
        ) : (
          <Text color="fg.secondary">Failed to load agent details</Text>
        )}
      </Flex>
    </Sidebar>
  );
}
